import type { IndicatorType, SentimentType } from "../market-view/analysis/sentiment";

export function categorizeStringAsSentiment(str: string): {
	indicator: IndicatorType;
	sentiment: SentimentType;
} | null {
	const normalisedStr = str.toLowerCase().replace(/[ -]/g, " ");
	switch (normalisedStr) {
		case "low risk":
		case "low risks":
			return { sentiment: "positive", indicator: "regime" };
		case "mid risk":
		case "mid risks":
			return { sentiment: "neutral", indicator: "regime" };
		case "high risk":
		case "high risks":
			return { sentiment: "negative", indicator: "regime" };
		case "low diversification":
		case "low diversifications":
			return { sentiment: "negative", indicator: "driver" };
		case "mid diversification":
		case "mid diversifications":
			return { sentiment: "neutral", indicator: "driver" };
		case "high diversification":
		case "high diversifications":
			return { sentiment: "positive", indicator: "driver" };
		case "high volatility":
			return { sentiment: "negative", indicator: "driver" };
		case "mid volatility":
			return { sentiment: "neutral", indicator: "driver" };
		case "low volatility":
			return { sentiment: "positive", indicator: "driver" };
		case "low return":
		case "low returns":
			return { sentiment: "negative", indicator: "driver" };
		case "mid return":
		case "mid returns":
			return { sentiment: "neutral", indicator: "driver" };
		case "high return":
		case "high returns":
			return { sentiment: "positive", indicator: "driver" };
		case "overweight":
			return { sentiment: "positive", indicator: "positioning" };
		case "strong overweight":
			return { sentiment: "super-positive", indicator: "positioning" };
		case "underweight":
			return { sentiment: "negative", indicator: "positioning" };
		case "strong underweight":
			return { sentiment: "super-negative", indicator: "positioning" };
		case "neutral":
			return { sentiment: "neutral", indicator: "positioning" };
		default:
			return null;
	}
}

/**
 * Matches one sentiment phrase standing alone in a run of prose, together with the single
 * punctuation character that may sit immediately before it and the one that may sit immediately
 * after — captured apart from the label so the replacement can re-emit them outside the badge.
 *
 * The four captures, in order: the boundary ahead of the phrase (the start of the text, or the one
 * whitespace character preceding it), the leading punctuation, the label, and the trailing
 * punctuation. The boundary ahead is consumed and put back by the replacement; the boundary behind
 * is a lookahead, so two phrases separated by a single space both match.
 *
 * The separator between the two words of a compound phrase is one space or one hyphen — the set
 * `categorizeStringAsSentiment` normalises — so every label this matches is a label it maps.
 *
 * A fresh pattern per scan: the caller rewrites the text the pattern is run against.
 */
const sentimentRegex = () =>
	/(^|\s)([,.;:"'-]?)((?:strong[ -])?(?:over|under)weight|neutral|(?:low|mid|high)[ -](?:risks?|diversifications?|returns?|volatility))([,.;:"'-]?)(?=\s|$)/i;
export function applySentimentBadges(text: string): string {
	let regex = sentimentRegex();
	let match;
	let offset = 0;
	let sliced = text;
	while ((match = regex.exec(sliced)) != null && match.index != null) {
		const wholeMatch = match[0];
		const leadingBoundary = `${match[1] ?? ""}`;
		const beginPunctuation = `${match[2] ?? ""}`;
		const label = match[3];
		const endPunctuation = `${match[4] ?? ""}`;

		const data = categorizeStringAsSentiment(label);
		if (!data) {
			// just jump over without altering the original content
			offset += match.index + wholeMatch.length;
		} else {
			// prepare the replacement string
			const replacement = `${leadingBoundary}${beginPunctuation}<template data-custom-element="sentiment" data-sentiment="${data.sentiment}" data-indicator="${data.indicator}" data-label="${label}"></template>${endPunctuation}`;

			const newBegin = `${text.slice(0, offset + match.index)}${replacement}`;
			text = `${newBegin}${text.slice(offset + match.index + wholeMatch.length)}`;
			// adjust offset accordingly
			offset = newBegin.length;
		}
		sliced = text.slice(offset);
		regex = sentimentRegex(); // reset cursor
	}

	return text;
}

export function sentimentToString(indicator: "regime" | "positioning", sentiment: SentimentType): string {
	switch (indicator) {
		case "regime":
			switch (sentiment) {
				case "super-positive":
					return "Low Risk";
				case "positive":
					return "Low Risk";
				case "neutral":
					return "Mid Risk";
				case "negative":
					return "High Risk";
				case "super-negative":
					return "High Risk";
			}
			break;
		case "positioning":
			switch (sentiment) {
				case "super-positive":
					return "Strong Overweight";
				case "positive":
					return "Overweight";
				case "neutral":
					return "Neutral";
				case "negative":
					return "Underweight";
				case "super-negative":
					return "Strong Underweight";
			}
			break;
	}
	throw new Error(`unknown mapping for indicator "${indicator}" and sentiment "${sentiment}"`);
}
